import React from 'react';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * Feb 2026 — Standardized mobile list card.
 *
 * Standard hierarchy locked by the Feb 2026 mobile spec:
 *   line 1 → primary title  (e.g., `#Order`)  + status pill
 *   line 2 → business name  + customer name
 *   line 3 → amount / meta  + date
 *
 * Slots are strings/nodes (already formatted by the caller) so a
 * given list page can pass whatever fits — the card just enforces
 * spacing, tap-target, typography and the "More" dropdown.
 *
 * Props
 * -----
 *   title            (node)      – bold left cell in line 1
 *   statusPill       (node)      – small pill on the right of line 1
 *   businessName     (node)      – bigger left cell in line 2
 *   customerName     (node)      – small right cell in line 2
 *   amount           (node)      – left cell in line 3 (bold)
 *   date             (node)      – right cell in line 3 (muted)
 *   onClick          (fn)        – card tap → open detail
 *   actions          ([{label,icon?,onClick,danger?}])
 *                                – dropdown items (rendered in a "More" menu)
 *   testId           (str)       – data-testid to hang tests off
 */
const MobileListCard = ({
  title,
  statusPill,
  businessName,
  customerName,
  amount,
  date,
  onClick,
  actions,
  testId,
  children,
}) => {
  const handleCardClick = (e) => {
    // Don't fire onClick when the More menu is tapped.
    if (e.target.closest('[data-more-menu="true"]')) return;
    if (onClick) onClick(e);
  };
  const stop = (e) => e.stopPropagation();

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      }}
      data-testid={testId}
      className={`
        w-full rounded-2xl border border-neutral-200 bg-white shadow-sm
        active:shadow-none active:scale-[0.995] transition
        px-4 py-3
        ${onClick ? 'cursor-pointer' : ''}
      `.trim()}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-bold text-[#0F172A] leading-tight truncate">
            {title}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {statusPill}
          {actions && actions.length > 0 && (
            <div data-more-menu="true" onClick={stop}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="More actions"
                    data-testid={testId ? `${testId}-more` : undefined}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-neutral-500 hover:text-[#0F172A] hover:bg-neutral-100 active:bg-neutral-200"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={6} className="w-52">
                  {actions.map((a, i) => {
                    if (a.divider) return <DropdownMenuSeparator key={`div-${i}`} />;
                    const Icon = a.icon;
                    return (
                      <DropdownMenuItem
                        key={a.label}
                        onSelect={(e) => {
                          e.preventDefault();
                          if (a.onClick) a.onClick();
                        }}
                        className={`flex items-center gap-2 py-2 text-sm ${
                          a.danger ? 'text-red-600 focus:text-red-700 focus:bg-red-50' : ''
                        }`}
                        data-testid={a.testId}
                      >
                        {Icon ? <Icon className="w-4 h-4" /> : null}
                        <span>{a.label}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {(businessName || customerName) && (
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <div className="text-sm font-semibold text-[#0F172A] truncate min-w-0 flex-1">
            {businessName}
          </div>
          {customerName && (
            <div className="text-xs text-neutral-500 truncate shrink-0 max-w-[45%] text-right">
              {customerName}
            </div>
          )}
        </div>
      )}

      {(amount || date) && (
        <div className="flex items-baseline justify-between gap-3">
          {amount && (
            <div className="text-sm font-bold text-[#0078D4]">
              {amount}
            </div>
          )}
          {date && (
            <div className="text-[11px] text-neutral-500">
              {date}
            </div>
          )}
        </div>
      )}

      {children && <div className="mt-2 pt-2 border-t border-neutral-100">{children}</div>}
    </div>
  );
};

export default MobileListCard;
